import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Loader } from 'semantic-ui-react';
import Tooltip from '@mui/material/Tooltip';
import { Box, Grid, ToggleButtonGroup, ToggleButton, Pagination, TextField } from '@mui/material/';
import Autocomplete from '@mui/material/Autocomplete';

import { getCurrentOrg, listWorkflows, listWorkflowsFilters } from '../../api/api';
import StripedTable from '../../components/Table/StripedTable.jsx';
import ErrorMessage from '../../components/Error/Error';
import { useUserState, useUserDispatch, workflowSelected } from '../../context/UserContext';
import { useDidMountEffect } from '../../utils/utils';

const Home = () => {
  const navigate = useNavigate();
  const user = useUserState();
  const userDispatch = useUserDispatch();

  const [rows, setRows] = useState();
  const [loading, setLoading] = useState(true);
  const [, setInitialRows] = useState();
  const [errorStatus, setErrorStatus] = useState();
  const [errorDetail, setErrorDetail] = useState();
  const [hasError, setHasError] = useState(false);
  const [firstRun, setFirstRun] = useState(true);

  // filter options
  const [tagsArr, ] = useState([]);
  // table filter
  const [searchedTags, setSearchedTags] = useState([] || null);
  // pagination
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [noOfPages, setNoOfPages] = useState();

  const { getAccessTokenSilently, logout } = useAuth0();

  useEffect(() => {
    console.log('CONTEXT_API_UPDATED -> ', user);
  }, [user]);

  useEffect(() => {

    setLoading(true);

    if (firstRun) {
      (async () => {
        getCurrentOrg( { logout, getAccessTokenSilently })
        .then( (response) => {
          window.localStorage.setItem('current_org', JSON.stringify(response.data));
        }
        )
        .catch()
        .finally(() => {
          let cleanHrefUrl = window.location.href.split('//')[1];
          if (cleanHrefUrl.slice(-1) === '/')
            cleanHrefUrl = cleanHrefUrl.slice(0, -1);
  
          if (window.location.host !== cleanHrefUrl) {
            navigate(`/${cleanHrefUrl.substring(cleanHrefUrl.indexOf('/')+1)}`);
          } else {
            getFilters()
            getPayloads();
            setFirstRun(false)
          }
        })
      })();
    } else {
      getPayloads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchedTags]);

  useDidMountEffect(() => {
    if (page !== 1) {
      setPage(1);
    } else {
      setLoading(true);
      getPayloads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsPerPage]);

  const getPayloads = () => {
    listWorkflows(
      { logout, getAccessTokenSilently },
      itemsPerPage,
      page - 1,
      searchedTags
    )
      .then((result) => {
        setInitialRows(result.data.content);
        setRows(result.data.content);
        setNoOfPages(result.data.totalPages);
      })
      .catch((err) => {
        setHasError(true)
        setErrorStatus(err.response.data.status);
        setErrorDetail(err.response.data.detail);
      })
      .finally(() => {
        finishLoader();
      });
  };
  
  const getFilters = () => {
    listWorkflowsFilters({ logout, getAccessTokenSilently })
    .then((response) => {

      let tags = response.data.find((el) => el.name === 'tags' )
      tags?.values?.forEach((el, index) => {
        tagsArr.push({
          key: index,
          value: el,
          text: el,
        });
      })
      
    })
    .catch((err) => {
      setHasError(true)
      setErrorStatus(err.response.status);
      setErrorDetail(err.response.data.detail);
    });
  }

  const handleItemsPerPage = (event, newAmount) => {
    setItemsPerPage(newAmount);
  };

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  const payloadTab = (riid) => {
    workflowSelected(userDispatch, riid);
    navigate(`workflows/${riid}`);
  };

  const finishLoader = () => {
    setLoading(false);
  };

  const setTag = (newTag) => {
    let tags = [];
    tags.push(newTag);
    setSearchedTags(tags);
  };

  return (
    <div className='home'>
      {rows?.length === undefined && !hasError ? (
        <div className='empty-table'>
          <Loader active indeterminate size='small' />
        </div>
      ) : (
        <>
          {hasError ? (
            <ErrorMessage statusCode={errorStatus} detail={errorDetail}></ErrorMessage>
          ) : (
            <Box
                sx={{
                  width: '100%',
                  border: '1px solid lightgrey',
                  borderRadius: '4px',
                  padding: '20px',
                  marginBottom: '20px',
                  display: 'block',
                }}
              >
                <div className='Home'>
                  <section className='filter-section'>
                    <Autocomplete
                      id='tags'
                      multiple
                      disableCloseOnSelect
                      limitTags={1}
                      options={tagsArr}
                      renderInput={(params) => (
                        <TextField {...params} label='Search by Tag' />
                      )}
                      getOptionLabel={(option) => option.text}
                      defaulvalue={searchedTags}
                      onChange={(_event, newTag) => {
                        setTag(newTag);
                      }}
                    />
                  </section>
                  {rows?.length !== undefined && !loading ? (
                    <div className='content'>
                      <StripedTable
                        aria-label='simple table'
                        rowsClickable
                        headings={[
                          'Title',
                          'RIID',
                          'Active Payloads',
                          'Total Payloads',
                          'Tags',
                        ]}
                        rows={rows?.map((data, index) => {
                          if (data) {
                            return {
                              elements: [
                                <p>{data.workflowDefinition.title}</p>,
                                <p>{data.workflowDefinition.riid}</p>,
                                <p>{data.activePayloads}</p>,
                                <p>{data.totalPayloads}</p>,
                                <p>
                                  {data.workflowDefinition.tags.map(
                                    (tag, index) => {
                                      return data.workflowDefinition.tags
                                        ?.length ? (
                                        <span key={index}>{tag}</span>
                                      ) : (
                                        <>
                                          <span key={index}>-</span>
                                        </>
                                      );
                                    }
                                  )}
                                </p>,
                              ],
                              onClick: () => {
                                payloadTab(data.workflowDefinition.riid);
                              },
                            };
                          } else {
                            return {
                              elements: [],
                            };
                          }
                        })}
                      />
                      
                      <Grid container spacing={2}>
                        <Grid item md={4}/>
                          <Grid item md={4}>
                            <Box
                              component='div'
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '3rem 0'
                              }}
                              >
                              <Pagination
                                count={noOfPages}
                                page={page}
                                onChange={handleChangePage}
                                defaultPage={0}
                                color='primary'
                                size='small'
                              />
                            </Box>
                          </Grid>
                          {rows?.length > 0 &&
                            <Grid item md={4} p={2}>
                              <Box
                                component='div'
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'right',
                                  alignItems: 'center',
                                  padding: '3rem 0'
                                }}
                                className='perpage'>
                                <Tooltip
                                  placement={'right'}
                                  arrow
                                  title={<span>View per page</span>}
                                >
                                  <ToggleButtonGroup
                                    value={itemsPerPage}
                                    exclusive
                                    onChange={handleItemsPerPage}
                                    aria-label='text alignment'
                                    size='small'
                                  >
                                    <ToggleButton value={10} aria-label='left aligned'>
                                      10
                                    </ToggleButton>
                                    <ToggleButton value={50} aria-label='centered'>
                                      50
                                    </ToggleButton>
                                    <ToggleButton value={100} aria-label='justified'>
                                      100
                                    </ToggleButton>
                                  </ToggleButtonGroup>
                                </Tooltip>
                              </Box>
                            </Grid>
                          }
                        </Grid>
                      </div>
                  ) : (
                    <>
                      {rows?.length > 0 ? (
                        <div className='empty-table'>
                          <Loader active indeterminate size='small' />
                        </div>
                      ) : (
                        <StripedTable rows={[]}></StripedTable>
                      )}
                    </>
                  )}
                </div>
            </Box>
          )}
        </>
      )}
    </div>
  );
};

export default Home;